import {
  onIdTokenChanged,
  signInWithCustomToken,
  signOut,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { clearGxlAuthCookie } from "./cookie";
import {
  createServerSession,
  type CreateServerSessionResult,
  deleteServerSession,
  hasValidServerSession,
  trySessionBootstrap as defaultTrySessionBootstrap,
} from "./serverSession";

export interface GxlSignedInPayload {
  firebaseUser: FirebaseUser;
  idToken: string;
  /** Set when sign-in came from Identity-Aware Proxy auto-login; Firebase user may not have email filled in yet. */
  iapEmail: string | null;
}

export interface UseGxlFirebaseAuthSyncOptions {
  auth: Auth | null;
  tryIapLogin?: () => Promise<{ firebase_token: string; email: string } | null>;
  trySessionBootstrap?: () => Promise<{
    firebase_token: string;
    email: string;
  } | null>;
  onSignedIn: (payload: GxlSignedInPayload) => void | Promise<void>;
  onSignedOut: () => void | Promise<void>;
}

const ensureDurableSession = async (
  idToken: string,
): Promise<CreateServerSessionResult> => {
  const first = await createServerSession(idToken);
  if (first.status === "created" || first.status === "auth_error") {
    return first;
  }
  // Transient failures: retry once before treating as a hard session failure.
  const second = await createServerSession(idToken);
  return second;
};

/**
 * GXL runs several web apps on different subdomains. Firebase remembers login
 * separately for each subdomain. A shared, HttpOnly server cookie provides a
 * seven-day sliding cross-subdomain session. Firebase alone is not enough: if
 * the durable session cannot be minted or renewed, the user is signed out.
 */
export function useGxlFirebaseAuthSync(
  options: UseGxlFirebaseAuthSyncOptions,
): { firebaseUser: FirebaseUser | null } {
  const {
    auth,
    tryIapLogin: tryIapLoginOpt,
    trySessionBootstrap: tryBootstrapOpt,
    onSignedIn,
    onSignedOut,
  } = options;

  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const hasServerSessionRef = useRef(false);
  const iapEmailRef = useRef<string | null>(null);
  const suppressHydrationRef = useRef(false);

  const onSignedInRef = useRef(onSignedIn);
  const onSignedOutRef = useRef(onSignedOut);
  const tryIapLoginRef = useRef(tryIapLoginOpt);
  const tryBootstrapRef = useRef(tryBootstrapOpt ?? defaultTrySessionBootstrap);
  onSignedInRef.current = onSignedIn;
  onSignedOutRef.current = onSignedOut;
  tryIapLoginRef.current = tryIapLoginOpt;
  tryBootstrapRef.current = tryBootstrapOpt ?? defaultTrySessionBootstrap;

  useEffect(() => {
    if (!auth) return;

    // Remove the old JavaScript-readable bearer-token cookie after deployment.
    clearGxlAuthCookie();

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        const idToken = await user.getIdToken();
        const sessionResult = await ensureDurableSession(idToken);
        if (sessionResult.status !== "created") {
          hasServerSessionRef.current = false;
          if (sessionResult.status === "auth_error") {
            console.error(
              "[Auth] Durable session required but could not be created:",
              sessionResult.detail ?? "unauthorized",
            );
            // Clear the cookie before signOut. Otherwise onIdTokenChanged(null)
            // bootstraps from the leftover cookie and signs the user back in.
            suppressHydrationRef.current = true;
            await deleteServerSession(idToken);
          } else {
            console.error(
              "[Auth] Durable session unavailable after retry:",
              sessionResult.status,
            );
          }
          await signOut(auth);
          return;
        }
        hasServerSessionRef.current = true;
        await Promise.resolve(
          onSignedInRef.current({
            firebaseUser: user,
            idToken,
            iapEmail: iapEmailRef.current,
          }),
        );
      } else {
        // Firebase says nobody is signed in on this subdomain. Maybe we’re behind Google IAP and should
        // get a token from the server, or maybe the shared cookie is still there from another GXL app.
        // Skip hydration after a durable-session auth_error so a leftover cookie cannot loop.
        if (!suppressHydrationRef.current) {
          if (tryIapLoginRef.current) {
            console.debug("[Auth] No Firebase user, checking for IAP...");
            const iapResponse = await tryIapLoginRef.current();
            if (iapResponse) {
              iapEmailRef.current = iapResponse.email;
              try {
                await signInWithCustomToken(auth, iapResponse.firebase_token);
                return;
              } catch (error) {
                console.error("[Auth] IAP auto-login failed:", error);
              }
            }
          }

          // A valid server session can hydrate Firebase on a new GXL subdomain.
          const bootstrapResponse = await tryBootstrapRef.current();
          if (bootstrapResponse) {
            try {
              await signInWithCustomToken(auth, bootstrapResponse.firebase_token);
              hasServerSessionRef.current = true;
              return;
            } catch (error) {
              console.error("[Auth] Session bootstrap sign-in failed:", error);
            }
          }
        }
        suppressHydrationRef.current = false;

        iapEmailRef.current = null;
        hasServerSessionRef.current = false;
        await Promise.resolve(onSignedOutRef.current());
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const authHandle = auth;
    const checkSession = async () => {
      if (document.visibilityState !== "visible") return;
      if (!hasServerSessionRef.current || !authHandle.currentUser) return;
      const sessionIsValid = await hasValidServerSession();
      if (sessionIsValid === false) {
        hasServerSessionRef.current = false;
        await signOut(authHandle);
      }
    };
    const onVisible = () => void checkSession();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [auth]);

  return { firebaseUser };
}
